import type { EquipmentAvailabilityDay } from "@eisox/api/core-api";
import dayjs from "@eisox/dayjs";
import { TableV2, Typography } from "@eisox/design-system";

import { EMPTY_VALUE } from "~/constants/display.constant";
import { m } from "~/paraglide/messages";

import { CONNECTIVITY_STATES, PING_KINDS } from "../connectivity-history.constant";
import { dayEvents, formatDuration } from "../helpers/connectivity-history.helper";

interface ConnectivityDayEventsProps {
  day: EquipmentAvailabilityDay;
  timezone: string;
}

const ConnectivityDayEvents: React.FC<ConnectivityDayEventsProps> = ({ day, timezone }) => {
  const events = dayEvents(day);

  return (
    <section className="gap-2.5 flex flex-col">
      <Typography as="h3" className="font-semibold text-lg">
        {m.connectivity_history_day_events({ date: dayjs(day.start).tz(timezone).format("dddd L") })}
      </Typography>
      <TableV2.Container>
        <TableV2.Root className="table-fixed">
          <colgroup>
            <col className="w-7.5" />
            <col className="md:w-40 w-24" />
            <col />
            <col className="md:w-40 w-24" />
          </colgroup>
          <TableV2.Header className="border-b-2 border-primary-darker-gray">
            <TableV2.Row>
              <TableV2.Head />
              <TableV2.Head className="pb-2.5 text-left">{m.date()}</TableV2.Head>
              <TableV2.Head className="pb-2.5 text-left">{m.type()}</TableV2.Head>
              <TableV2.Head className="pb-2.5 text-left">{m.connectivity_history_duration()}</TableV2.Head>
            </TableV2.Row>
          </TableV2.Header>
          <TableV2.Body>
            {events.length === 0 ? (
              <TableV2.Row>
                <TableV2.Cell colSpan={4} className="py-5 text-center text-primary-dark-gray">
                  {m.connectivity_history_no_event()}
                </TableV2.Cell>
              </TableV2.Row>
            ) : (
              events.map(event => {
                const { label, color } = "end" in event ? CONNECTIVITY_STATES[event.kind] : PING_KINDS[event.kind];
                return (
                  <TableV2.Row
                    key={`${event.kind}-${event.start}`}
                    className="border-b border-primary-gray last:border-0"
                  >
                    <TableV2.Cell className="py-2.5">
                      <span className="size-2.5 block rounded-full" style={{ backgroundColor: color }} />
                    </TableV2.Cell>
                    <TableV2.Cell className="py-2.5 tabular-nums">
                      {dayjs(event.start).tz(timezone).format("LTS")}
                    </TableV2.Cell>
                    <TableV2.Cell className="py-2.5">{label()}</TableV2.Cell>
                    <TableV2.Cell className="py-2.5 tabular-nums">
                      {"end" in event ? formatDuration(dayjs(event.end).diff(event.start)) : EMPTY_VALUE}
                    </TableV2.Cell>
                  </TableV2.Row>
                );
              })
            )}
          </TableV2.Body>
        </TableV2.Root>
      </TableV2.Container>
    </section>
  );
};

ConnectivityDayEvents.displayName = "ConnectivityDayEvents";

export { ConnectivityDayEvents };
